import equal from "fast-deep-equal";
import { DateTime } from "luxon";

import type { AssignmentIntervalPage } from "@storyboard/web-client";
import { serverNow } from "@storyboard/web-client";
import { ASSIGNMENT_STALE_AFTER } from "@/models/tmsEvent/assignmentConstants";
import { attributedMembershipIdsOf, directMembershipIdOf } from "@/models/tmsEvent/attribution";
import { idx, rowsByIndex, store } from "@/store/store";
import type { AssignmentIntervalRow, TmsEventRow } from "@/store/types";

/**
 * Upserts a page's records by `openedBy`; identical rows are skipped so a delta's overlap is free.
 *
 * History and delta pages land the same way: a record's identity never changes, so one served again after it closed
 * updates in place, and one the store already holds field for field is left untouched. Every record that did change
 * re-attributes the stored events on its unit.
 */
export function upsertAssignmentIntervals(page: AssignmentIntervalPage): void {
  if (page.intervals.length === 0) return;
  const before = page.intervals.map((row) => store.get("assignmentInterval", row.openedBy));
  // The store hands back the row it already held for an unchanged record, so identity tells the changed ones apart.
  const changed = store.upsert("assignmentInterval", page.intervals).filter((row, i) => row !== before[i]);
  if (changed.length > 0) rederiveEventsOn(changed);
}

/**
 * Whether an active record is a missed logout as of `now`: open for longer than `ASSIGNMENT_STALE_AFTER`.
 *
 * A stale record covers only the instants before its start plus the threshold, never the present.
 */
export function isStaleAt(row: AssignmentIntervalRow, now: string): boolean {
  if (row.status !== "active") return false;
  return DateTime.fromISO(now) >= staleFrom(row);
}

/**
 * Records on the unit, within the integration, covering `at`: `start <= at < end`, or `end` null and the record not
 * stale at `at` as of `now`.
 */
export function intervalsCovering(
  vendorVehicleKey: string,
  workspaceIntegrationId: string,
  at: string,
  now: string,
): AssignmentIntervalRow[] {
  const instant = DateTime.fromISO(at);
  return rowsByIndex("assignmentInterval", idx.assignmentIntervalsByVehicle, vendorVehicleKey).filter(
    (row) => row.workspaceIntegrationId === workspaceIntegrationId && covers(row, instant, now),
  );
}

/** The first instant a record left open stops covering. */
function staleFrom(row: AssignmentIntervalRow): DateTime {
  return DateTime.fromISO(row.start).plus(ASSIGNMENT_STALE_AFTER);
}

/** Whether one record covers `instant`, read as of `now`. */
function covers(row: AssignmentIntervalRow, instant: DateTime, now: string): boolean {
  // The start is inclusive: a record covers the instant it opened at.
  if (instant < DateTime.fromISO(row.start)) return false;
  // A closed record ends exclusively: the closing instant belongs to whatever opened then, such as a swap's re-attach.
  if (row.end !== null) return instant < DateTime.fromISO(row.end);
  // An open record covers everything after its start until it reads as a missed logout; a stale one keeps only the
  // instants before the threshold.
  return !isStaleAt(row, now) || instant < staleFrom(row);
}

/**
 * Re-attributes the stored events on the units whose records changed, writing only those whose drivers changed.
 *
 * An event with a direct reference never reads the records, so it is skipped. One that loses a driver is deleted and
 * re-inserted rather than updated: a keyed compute finds the feeds an update touches from the new row alone, and
 * would never revisit the feed of the driver the event left.
 */
function rederiveEventsOn(changed: readonly AssignmentIntervalRow[]): void {
  const now = serverNow().toISOString();
  const covering = (vehicleKey: string, integrationId: string, at: string) =>
    intervalsCovering(vehicleKey, integrationId, at, now);
  const gained: TmsEventRow[] = [];
  const lost: TmsEventRow[] = [];

  // Recompute every event on each touched unit, keeping those whose drivers moved.
  for (const vehicleKey of new Set(changed.map((row) => row.vendorVehicleKey))) {
    for (const event of rowsByIndex("tmsEvent", idx.tmsEventsByVehicle, vehicleKey)) {
      if (directMembershipIdOf(event) !== null) continue;
      const ids = attributedMembershipIdsOf(event, covering);
      if (equal(ids, event.attributedMembershipIds)) continue;
      const row = { ...event, attributedMembershipIds: ids };
      if (event.attributedMembershipIds.some((id) => !ids.includes(id))) lost.push(row);
      else gained.push(row);
    }
  }

  // One batch, so every listener sees the re-attribution land at once.
  store.batch(() => {
    store.delete(
      "tmsEvent",
      lost.map((row) => row.id),
    );
    store.upsert("tmsEvent", [...lost, ...gained]);
  });
}
